import React, {
    AriaRole,
    FC,
    ForwardedRef,
    InputHTMLAttributes,
    LabelHTMLAttributes,
    ReactNode,
    RefObject,
} from 'react';
import { mergeProps, PressEvent, useHover, usePress, VisuallyHidden } from 'react-aria';
import { isMobile } from '@direct-frontend/platform-code';
import { castRef } from '@direct-frontend/stdlib';

import { useHStack } from '../../hooks/useStack';
import { CheckboxSize } from '../Checkbox/types';
import { TextTruncationProps } from '../TextGeneric/TextGeneric';
import { HelpHandler } from '../Help';
import { cn } from '../../lib/css/className/className';
import { TestProps } from '../../internal/types/testTypes';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import './ToggleBase.css';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { useFocusOptions } from '../../hooks/useFocusOptions';

export interface ToggleBaseProps extends HelpHandler, TextTruncationProps {
    /**
     * Ссылка на нативный input
     */
    inputRef: RefObject<HTMLInputElement>;

    /**
     * Ссылка на label
     */
    labelRef?: ForwardedRef<HTMLElement>;

    /**
     * Размер элемента
     */
    size: CheckboxSize;

    /**
     * Переключение жирного шрифта подписи
     */
    isBold?: boolean;

    /**
     * Подстрочный текст к подписи
     */
    caption?: ReactNode;

    /**
     * Содержимое тултипа, открывающегося по клику на иконку вопросика
     */
    tooltipContent?: ReactNode;

    /**
     * Рендер кастомного бокса
     */
    children: (props: { isFocused?: boolean; isHovered?: boolean }) => ReactNode;

    /**
     * Дополнительный класс для элемента
     */
    className?: string;

    /**
     * Признак выключенного контрола
     */
    isDisabled?: boolean;

    /**
     * Подпись контрола
     */
    label?: ReactNode;

    /**
     * Опции для нативного input
     */
    inputProps: InputHTMLAttributes<HTMLInputElement> & TestProps;

    /**
     * Опции для нативного label
     */
    labelProps: LabelHTMLAttributes<HTMLLabelElement>;

    /**
     * tabIndex для компонента
     */
    tabIndex?: number;

    /**
     * Обработчик нажатия на контрол, если нужно обработать специфичные кейсы типа клика с Shift.
     * Для всех остальных кейсов используйте onChange
     */
    onPress?: (event: PressEvent) => void;

    /**
     * ARIA-атрибут роли
     * @default checkbox
     */
    role?: AriaRole;
}

export type ToggleProps = Omit<ToggleBaseProps, 'children' | 'inputRef' | 'inputProps'>;

const cnToggleBase = cn('ToggleBase');

/**
 * Базовый компонент для создания подписей для checkbox и radiobox
 * @internal
 */
export const ToggleBase: FC<ToggleBaseProps> = function ToggleBase(props) {
    const {
        size,
        caption,
        help,
        isDisabled,
        className,
        children,
        label,
        inputRef,
        labelRef,
        inputProps: { 'data-testid': dataTestId = 'ToggleBase', ...inputProps },
        labelProps,
        isTruncated,
        maxLines,
        tabIndex,
    } = props;

    const { hoverProps, isHovered } = useHover({ ...props, isDisabled: false });
    // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
    const { focusProps, isFocusVisible } = useFocusOptions();
    const stackClass = useHStack({ gap: 'current-geometry' });

    const sizeSkinClass = getSizeSkin(size);
    const skinClass = getColorSkin('gray-transparent', { isHovered, isDisabled });

    const textSizeWithSkin = 'current-main + current-body';

    const { addonClasses } = useInternalVerticalAlignWithSkin({
        textSize: textSizeWithSkin,
        addonType: 'icon',
    });

    const hasLabel = Boolean(label || caption || help);

    const { pressProps } = usePress({ onPress: props.onPress });

    const content = (
        <>
            <div className={hasLabel ? addonClasses : undefined}>
                {children({ isFocused: isFocusVisible, isHovered })}
            </div>
            <VisuallyHidden>
                <input
                    {...inputProps}
                    {...focusProps}
                    data-testid={`${dataTestId}.input`}
                    ref={inputRef}
                    tabIndex={tabIndex}
                />
            </VisuallyHidden>
            {hasLabel ? (
                <TextGenericWithSkin
                    color={'current'}
                    caption={caption}
                    isTruncated={isTruncated}
                    maxLines={maxLines}
                    help={help}
                    bold={props.isBold}
                    textSize={textSizeWithSkin}
                    skinSize={size}
                >
                    {label}
                </TextGenericWithSkin>
            ) : null}
        </>
    );

    if (isMobile()) {
        return (
            <div
                {...mergeProps(hoverProps, labelProps, props.onPress ? pressProps : undefined)}
                data-testid={`${dataTestId}.label`}
                ref={castRef(labelRef, 'div')}
                className={cnToggleBase({ disabled: isDisabled }, [sizeSkinClass, skinClass, stackClass, className])}
            >
                {content}
            </div>
        );
    }

    return (
        <label
            {...mergeProps(hoverProps, labelProps, props.onPress ? pressProps : undefined)}
            data-testid={`${dataTestId}.label`}
            ref={castRef(labelRef, 'label')}
            className={cnToggleBase({ disabled: isDisabled }, [sizeSkinClass, skinClass, stackClass, className])}
        >
            {content}
        </label>
    );
};
